import type { FeatureFlagService } from "../../../services/feature-flags";
import type { TraceScene, TraceScenePath } from "./types";

/**
 * The phase walk. Doc 04 section 10.3.
 *
 * "For each phase whose `bindItemId` matches the current item, that phase's
 * markers fade in and a pointer ring animates along `path` at a speed matched
 * to the length of the spoken phase text."
 *
 * Doc 04 calls this the feature with the highest training value in R1, and
 * doc 03 section 6.2 says why: the card holds only the general scheme of
 * manoeuvre, and the detailed version does not exist in the document at all.
 * It is expanded live off the trace, from each phase's `detail` field, spoken
 * while the pointer walks that phase's path.
 *
 * THE MECHANISM, NOT THE DATA. No real phase data exists. Everything here is
 * driven by a sidecar the caller supplies, and the tests drive it from an
 * invented fixture. Nothing in this module knows anything about any real
 * scenario, and it contains no doctrinal content.
 *
 * Behind the `phaseWalk` flag, default off.
 */

export interface PhaseWalkPoint {
  readonly x: number;
  readonly y: number;
}

export interface PhaseWalkFrame {
  /** True once the pointer has reached the end of the path. */
  readonly complete: boolean;
  readonly pointer: PhaseWalkPoint;
  /** 0 at the start of the phase, 1 at the end. */
  readonly progress: number;
}

/**
 * Doc 04 section 10.3: the pointer moves "at a speed matched to the length of
 * the spoken phase text", so the walk finishes as the narrative finishes.
 *
 * The figure is the speech engine's own characters-per-millisecond, passed in
 * rather than assumed here. There is no rate constant in this module: doc 03
 * section 2.1 owns delivery rates and this must not hold a second opinion.
 */
/**
 * Characters per spoken word, for turning a delivery rate into a pointer
 * speed.
 *
 * Doc 04 section 10.3 requires the walk to finish as the narrative finishes,
 * and the narrative is spoken at the delivery's own rate. The rate is in
 * words per minute and the path is walked per character, so the two need a
 * bridge, and this is it.
 *
 * Five characters plus a space is the long-standing convention for a word in
 * words-per-minute measurement. It is a **timing** convention and not
 * doctrine: nothing here decides what is said, only how fast a ring slides
 * along a line, and being a little out makes the ring arrive slightly early
 * or late rather than making anything wrong.
 */
const CHARACTERS_PER_WORD = 6;

/** Milliseconds per character at a delivery rate, for the pointer walk. */
export function msPerCharacterAt(targetWpm: number): number {
  if (targetWpm <= 0) return 0;
  return 60_000 / (targetWpm * CHARACTERS_PER_WORD);
}

export function walkDurationMs(detail: string, msPerCharacter: number): number {
  if (msPerCharacter <= 0) return 0;
  return detail.length * msPerCharacter;
}

/** Parses the scene's `points` string back into coordinates. */
export function pathPoints(path: TraceScenePath): readonly PhaseWalkPoint[] {
  return path.points
    .split(" ")
    .filter((pair) => pair !== "")
    .flatMap((pair) => {
      const parts = pair.split(",");
      if (parts.length !== 2) return [];
      const x = Number(parts[0]);
      const y = Number(parts[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return [];
      return [{ x, y }];
    });
}

interface Segment {
  readonly from: PhaseWalkPoint;
  readonly length: number;
  readonly to: PhaseWalkPoint;
}

/**
 * The polyline as concrete segments.
 *
 * Built by walking the points rather than by index, so every `from` and `to`
 * is a real point and the arithmetic below needs no fallback for an index
 * that cannot be out of range.
 */
function segmentsOf(points: readonly PhaseWalkPoint[]): readonly Segment[] {
  const segments: Segment[] = [];
  let previous: PhaseWalkPoint | undefined;
  for (const point of points) {
    if (previous !== undefined) {
      segments.push({
        from: previous,
        to: point,
        length: distance(previous, point),
      });
    }
    previous = point;
  }
  return segments;
}

function distance(from: PhaseWalkPoint, to: PhaseWalkPoint): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}

/**
 * The point a given fraction along a polyline, measured by arc length.
 *
 * By length rather than by segment index, so the pointer moves at a constant
 * speed over the ground instead of hurrying along short segments and crawling
 * along long ones. A trace path has very uneven segments.
 */
export function pointerAt(
  points: readonly PhaseWalkPoint[],
  progress: number,
): PhaseWalkPoint {
  const first = points[0];
  if (first === undefined) return { x: 0, y: 0 };

  const segments = segmentsOf(points);
  const total = segments.reduce((sum, segment) => sum + segment.length, 0);
  // A single point, or a path whose points coincide: no length to walk along.
  if (total === 0) return first;
  if (progress <= 0) return first;

  const lastSegment = segments[segments.length - 1];
  /* v8 ignore next -- a non-zero total proves there is at least one segment */
  if (lastSegment === undefined) return first;

  let travelled = progress * total;
  for (const segment of segments) {
    if (travelled <= segment.length) {
      const share = travelled / segment.length;
      return {
        x: segment.from.x + (segment.to.x - segment.from.x) * share,
        y: segment.from.y + (segment.to.y - segment.from.y) * share,
      };
    }
    travelled -= segment.length;
  }
  // Floating-point overshoot on the last segment.
  return lastSegment.to;
}

/**
 * Where the pointer is, for one phase, at a given point in its delivery.
 *
 * `elapsedMs` is the time since this phase's narrative began speaking. The
 * caller owns the clock; nothing here measures time.
 */
export function phaseWalkFrame(
  path: TraceScenePath,
  elapsedMs: number,
  msPerCharacter: number,
): PhaseWalkFrame {
  const duration = walkDurationMs(path.detail, msPerCharacter);
  const progress =
    duration <= 0 ? 1 : Math.min(1, Math.max(0, elapsedMs / duration));
  return {
    complete: progress >= 1,
    pointer: pointerAt(pathPoints(path), progress),
    progress,
  };
}

/**
 * The phases bound to the item currently being delivered. Doc 04 section
 * 10.3: "each phase whose `bindItemId` matches the current item".
 *
 * A phase bound to nothing, or to an item not in this document, simply never
 * matches. It is not an error and it is not drawn.
 */
export function phasesBoundTo(
  scene: TraceScene,
  itemId: string | null,
): readonly TraceScenePath[] {
  if (itemId === null || itemId === "") return [];
  return scene.paths.filter((path) => path.bindItemId === itemId);
}

/** The markers belonging to the phases currently being walked. */
export function markersForPhases(
  phases: readonly TraceScenePath[],
): readonly string[] {
  return [...new Set(phases.flatMap((phase) => phase.markerIds))];
}

/**
 * The narrative spoken while the pointer walks. Doc 03 section 6.2: the
 * detailed scheme of manoeuvre does not exist in the document, so this is the
 * only place it comes from.
 */
export function phaseNarrative(phases: readonly TraceScenePath[]): string {
  return phases
    .map((phase) => phase.detail.trim())
    .filter((detail) => detail !== "")
    .join(" ");
}

export interface PhaseWalkState {
  readonly markerIds: readonly string[];
  readonly narrative: string;
  readonly phases: readonly TraceScenePath[];
}

export const PHASE_WALK_OFF: PhaseWalkState = Object.freeze({
  markerIds: Object.freeze([]) as readonly string[],
  narrative: "",
  phases: Object.freeze([]) as readonly TraceScenePath[],
});

/**
 * What the walk should show for the item being delivered.
 *
 * **With the `phaseWalk` flag off this returns `PHASE_WALK_OFF`, and the
 * caller has nothing to draw and nothing extra to speak.** That is what keeps
 * the delivery path unchanged: the flag is checked here, once, before any
 * phase is looked at.
 */
export function phaseWalkFor(
  scene: TraceScene,
  itemId: string | null,
  flags: FeatureFlagService,
): PhaseWalkState {
  if (!flags.isEnabled("phaseWalk")) return PHASE_WALK_OFF;
  const phases = phasesBoundTo(scene, itemId);
  if (phases.length === 0) return PHASE_WALK_OFF;
  return {
    markerIds: markersForPhases(phases),
    narrative: phaseNarrative(phases),
    phases,
  };
}
